import type { SpotifyClient } from './client.ts';
import type {
  SpotifyDevice,
  SpotifyPlaybackState,
  SpotifyUser,
} from './types.ts';

export function getCurrentUser(client: SpotifyClient): Promise<SpotifyUser | null> {
  return client.get<SpotifyUser>('/me');
}

export async function getDevices(client: SpotifyClient): Promise<SpotifyDevice[]> {
  const result = await client.get<{ devices: SpotifyDevice[] }>('/me/player/devices');
  return result?.devices ?? [];
}

export function getPlaybackState(
  client: SpotifyClient,
): Promise<SpotifyPlaybackState | null> {
  // additional_types=episode, sonst liefert Spotify bei Podcasts item=null.
  return client.get<SpotifyPlaybackState>('/me/player', { additional_types: 'episode' });
}

export async function transferPlayback(
  client: SpotifyClient,
  deviceId: string,
  play = false,
): Promise<void> {
  await client.put('/me/player', { body: { device_ids: [deviceId], play } });
}

export interface PlayOptions {
  deviceId?: string;
  /** Album/Show/Playlist-URI, z. B. "spotify:album:..." */
  contextUri?: string;
  /** Einzelne Track-/Episode-URIs (Alternative zu contextUri). */
  uris?: string[];
  /** Index innerhalb des Kontexts (0-basiert). */
  offsetPosition?: number;
  /** Startposition innerhalb des Tracks in Millisekunden. */
  positionMs?: number;
}

export async function play(client: SpotifyClient, options: PlayOptions = {}): Promise<void> {
  const body: Record<string, unknown> = {};
  if (options.contextUri) body['context_uri'] = options.contextUri;
  if (options.uris?.length) body['uris'] = options.uris;
  if (options.offsetPosition !== undefined) {
    body['offset'] = { position: options.offsetPosition };
  }
  if (options.positionMs !== undefined) {
    body['position_ms'] = Math.max(0, Math.round(options.positionMs));
  }

  await client.put('/me/player/play', {
    query: options.deviceId ? { device_id: options.deviceId } : undefined,
    // Leerer Body = "einfach weiterspielen".
    body: Object.keys(body).length > 0 ? body : {},
  });
}

export async function pause(client: SpotifyClient, deviceId?: string): Promise<void> {
  await client.put('/me/player/pause', {
    query: deviceId ? { device_id: deviceId } : undefined,
  });
}

export async function seek(
  client: SpotifyClient,
  positionMs: number,
  deviceId?: string,
): Promise<void> {
  await client.put('/me/player/seek', {
    query: {
      position_ms: Math.max(0, Math.round(positionMs)),
      ...(deviceId ? { device_id: deviceId } : {}),
    },
  });
}

export async function setVolume(
  client: SpotifyClient,
  volumePercent: number,
  deviceId?: string,
): Promise<void> {
  await client.put('/me/player/volume', {
    query: {
      volume_percent: Math.min(100, Math.max(0, Math.round(volumePercent))),
      ...(deviceId ? { device_id: deviceId } : {}),
    },
  });
}
